<template>
  <div v-if="hasSwatches" class="ProductCard__SwatchSlider">
    <button
      v-if="hasButtons"
      role="button"
      data-direction="prev"
      class="ProductCard__SwatchBtn"
      :aria-disabled="atStart"
      :class="{ 'ProductCard__SwatchBtn--Disabled': atStart }"
      @click="move('-')"
    >
      <Icon icon="bookend-arrow" />
    </button>
    <Splide
      ref="swatchSlider"
      :options="config"
      @splide:resized="determinePosition"
      @splide:moved="determinePosition"
      @splide:mounted="determinePosition"
    >
      <SplideSlide
        v-for="swatch in swatches"
        :key="swatch"
        :class="swatchClasses(swatch)"
      >
        <button
          role="button"
          :aria-label="`Color: ${swatch}`"
          @click="selection(swatch)"
        >
          <div
            v-if="isHex(swatch)"
            role="presentation"
            :style="fetchSwatch(swatch)"
            data-ht-element="swatch"
            :data-ht-id="swatch"
          ></div>
          <img
            v-else
            :src="fetchSwatch(swatch)"
            data-ht-element="swatch"
            :data-ht-id="swatch"
            alt=""
            role="presentation"
          />
        </button>
      </SplideSlide>
    </Splide>
    <button
      v-if="hasButtons"
      role="button"
      data-direction="next"
      class="ProductCard__SwatchBtn"
      :aria-disabled="atEnd"
      :class="{ 'ProductCard__SwatchBtn--Disabled': atEnd }"
      @click="move('+')"
    >
      <Icon icon="bookend-arrow" />
    </button>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import { fetchSwatch, isHex } from './Composables/ProductCard.js';
import { SplideSlide, Splide } from '@splidejs/vue-splide';
import { pubSub } from '@utils/PubSub.js';
import Icon from '../Global/Icon.vue';

const swatchSlider = ref(null);
const atStart = ref(false);
const atEnd = ref(false);
const active = ref(0);
const selected = ref(false);
const hasSwatches = ref(false);

const emits = defineEmits(['updateCard']);
const props = defineProps({
  allOptions: Array,
  selectedOptions: Array,
  swatches: Array,
  url: String,
  selectionListener: Boolean,
  secondaryItems: {
    type: Array,
    default: [],
  },
  hiddenOptions: {
    type: Array,
    default: [],
  },
});

const config = {
  type: 'slide',
  gap: 6,
  arrows: false,
  pagination: false,
  height: 26,
  width: 'calc(100% - 48px)', //Accounts for button widths
  fixedWidth: 26,
  fixedHeight: 26,
};

const move = (direction) => {
  const slider = swatchSlider.value.splide.Components.Controller;
  const index = slider.getIndex();

  active.value = direction === '+' ? index + 4 : index - 4;

  return slider.go(active.value);
};

const determinePosition = (splide) => {
  setTimeout(() => {
    const end = splide.Components.Controller.getEnd();
    const slides = splide.Components.Elements.slides;

    const firstSlide = slides[0].classList;
    const lastSlide = slides[end].classList;

    atStart.value = firstSlide.contains('is-visible');
    atEnd.value = lastSlide.contains('is-visible');
  }, 0);
};

const hasButtons = computed(() => {
  const startVisible = atStart.value;
  const endVisible = atEnd.value;
  const bookendsVisibile = startVisible && endVisible;

  return bookendsVisibile === false;
});

const swatches = computed(() => {
  const values = props?.swatches ?? [];
  const secondaryItems = props?.secondaryItems ?? [];

  if (secondaryItems.length <= 0) {
    return values.filter((value) => {
      return !props.hiddenOptions.includes(value);
    });
  }

  const secondaryValues = secondaryItems.flatMap(
    (item) => item.swatches.values
  );

  const allValues = [...new Set(values), ...new Set(secondaryValues)];

  return allValues.filter((value) => {
    return !props.hiddenOptions.includes(value);
  });
});

const selection = (context = false) => {
  const options = props?.selectedOptions || false;
  if (!options) return;

  const color = options.filter((option) => option.name === 'Color')[0] || [];

  if (color.length <= 0) return;

  selected.value = context || color.value;

  const selectedSwatch = {
    name: color.name,
    value: selected.value,
  };
  emits('updateCard', selectedSwatch);
};

const listenForPDPSelections = () => {
  pubSub.subscribe('pdp:selection:update', ({ detail }) => {
    const { value } = detail;
    const flatOptions = props.allOptions.flatMap((option) => option.values);
    const hasMatchingValue = flatOptions.includes(value);
    if (hasMatchingValue) selection(value);
  });
};

const swatchClasses = (swatch) => {
  const isActive = selected.value === swatch;
  const inactive = `ProductCard__Swatch`;
  const active = `ProductCard__Swatch ProductCard__Swatch--Active`;
  return isActive ? active : inactive;
};

const displaySwatch = (swatch) => {
  const hiddenOptions = props?.hiddenOptions ?? [];
  const isVisible =
    hiddenOptions.filter((option) => option === swatch).length === 0;

  if (isVisible) return fetchSwatch(swatch);
  else return false;
};

onMounted(() => {
  hasSwatches.value = swatches.value.length > 0;
  if (!hasSwatches.value) return;

  selected.value = props.selectedOptions;

  if (props.selectionListener) {
    listenForPDPSelections();
  }
  selection();
});

watch(props.selectedOptions, () => {
  const color =
    props.selectedOptions.filter((option) => option.name === 'Color')[0] || [];
  if (color.length <= 0) return;
  selected.value = color.value;
});
</script>
